export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any;
  stack?: string;
  userAgent?: string;
  url?: string;
  userId?: string;
  sessionId?: string;
}

export class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private maxLogs = 1000; // Keep last 1000 logs in memory
  private logToServer = process.env.NODE_ENV === 'production';
  private serverEndpoint = '/api/v1/logs';
  private sessionId: string;

  private constructor() {
    this.sessionId = this.generateSessionId();
    this.setupGlobalErrorHandling();
    this.overrideConsoleMethods();
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private setupGlobalErrorHandling(): void {
    // Capture unhandled JavaScript errors
    window.addEventListener('error', (event) => {
      this.error('Unhandled JavaScript Error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack
      });
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.error('Unhandled Promise Rejection', {
        reason: event.reason,
        stack: event.reason?.stack
      });
    });
  }

  private overrideConsoleMethods(): void {
    const originalConsole = { ...console };

    console.log = (...args) => {
      originalConsole.log(...args);
      this.info('Console Log', { args });
    };

    console.info = (...args) => {
      originalConsole.info(...args);
      this.info('Console Info', { args });
    };

    console.warn = (...args) => {
      originalConsole.warn(...args);
      this.warn('Console Warning', { args });
    };

    console.error = (...args) => {
      originalConsole.error(...args);
      this.error('Console Error', { args });
    };

    console.debug = (...args) => {
      originalConsole.debug(...args);
      this.debug('Console Debug', { args });
    };
  }

  private createLogEntry(level: LogLevel, message: string, data?: any, stack?: string): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      stack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      sessionId: this.sessionId
    };
  }

  private getCurrentUserId(): string | undefined {
    // Try to get user ID from localStorage or context
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        return user.id?.toString();
      }
    } catch (e) {
      // Ignore errors
    }
    return undefined;
  }

  private addLog(entry: LogEntry): void {
    this.logs.push(entry);
    
    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Send to server if enabled
    if (this.logToServer) {
      this.sendToServer(entry);
    }

    // Also save to localStorage for debugging
    this.saveToLocalStorage();
  }

  private async sendToServer(entry: LogEntry): Promise<void> {
    try {
      await fetch(this.serverEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(entry)
      });
    } catch (error) {
      // Silently fail to avoid infinite loops
    }
  }

  private saveToLocalStorage(): void {
    try {
      const recentLogs = this.logs.slice(-100); // Save only last 100 logs
      localStorage.setItem('app_logs', JSON.stringify(recentLogs));
    } catch (e) {
      // Storage might be full, clear old logs
      try {
        localStorage.removeItem('app_logs');
        const recentLogs = this.logs.slice(-50);
        localStorage.setItem('app_logs', JSON.stringify(recentLogs));
      } catch (e) {
        // Ignore if still failing
      }
    }
  }

  // Public logging methods
  public debug(message: string, data?: any): void {
    const entry = this.createLogEntry(LogLevel.DEBUG, message, data);
    this.addLog(entry);
  }

  public info(message: string, data?: any): void {
    const entry = this.createLogEntry(LogLevel.INFO, message, data);
    this.addLog(entry);
  }

  public warn(message: string, data?: any): void {
    const entry = this.createLogEntry(LogLevel.WARN, message, data);
    this.addLog(entry);
  }

  public error(message: string, data?: any, error?: Error): void {
    const entry = this.createLogEntry(
      LogLevel.ERROR, 
      message, 
      data, 
      error?.stack
    );
    this.addLog(entry);
  }

  // API call logging
  public logAPICall(method: string, url: string, status: number, duration: number, error?: any): void {
    const level = status >= 400 ? LogLevel.ERROR : status >= 300 ? LogLevel.WARN : LogLevel.INFO;
    this.addLog(this.createLogEntry(level, 'API Call', {
      method,
      url,
      status,
      duration,
      error: error?.message
    }));
  }

  // Performance logging
  public logPerformance(name: string, duration: number, details?: any): void {
    const level = duration > 2000 ? LogLevel.WARN : LogLevel.INFO;
    this.addLog(this.createLogEntry(level, 'Performance Metric', {
      name,
      duration,
      details
    }));
  }

  // User action logging
  public logUserAction(action: string, details?: any): void {
    this.info('User Action', {
      action,
      details
    });
  }

  // Navigation logging
  public logNavigation(from: string, to: string): void {
    this.info('Navigation', {
      from,
      to
    });
  }

  // Component lifecycle logging
  public logComponentLifecycle(component: string, event: string, props?: any): void {
    this.debug('Component Lifecycle', {
      component,
      event,
      props
    });
  }

  // Get logs for debugging
  public getLogs(level?: LogLevel): LogEntry[] {
    if (level) {
      return this.logs.filter(log => log.level === level);
    }
    return [...this.logs];
  }

  // Export logs
  public exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  // Clear logs
  public clearLogs(): void {
    this.logs = [];
    localStorage.removeItem('app_logs');
  }

  // Download logs as file
  public downloadLogs(): void {
    const dataStr = this.exportLogs();
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `app-logs-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Convenience functions
export const logDebug = (message: string, data?: any) => logger.debug(message, data);
export const logInfo = (message: string, data?: any) => logger.info(message, data);
export const logWarn = (message: string, data?: any) => logger.warn(message, data);
export const logError = (message: string, data?: any, error?: Error) => logger.error(message, data, error);
export const logAPICall = (method: string, url: string, status: number, duration: number, error?: any) => 
  logger.logAPICall(method, url, status, duration, error);
export const logPerformance = (name: string, duration: number, details?: any) => 
  logger.logPerformance(name, duration, details);
export const logUserAction = (action: string, details?: any) => logger.logUserAction(action, details);
export const logNavigation = (from: string, to: string) => logger.logNavigation(from, to);

export default logger;